/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createColumnHelper, type CellContext } from '@tanstack/react-table';
import { IconLucide, Input, useTanstackTable } from '@yakit/ui-components';

import { VDLLevel0TableArrowPosition } from '@VDL/components/VDLLevel0Table/VDLLevel0TableArrowPosition';
import { VDLLevel0TableNumberPosition } from '@VDL/components/VDLLevel0Table/VDLLevel0TableNumberPosition';
import { VDLAgreementButton } from '@VDL/components/VDLAgreementButton';
import { VDLBaseTable } from '@VDL/components/VDLBaseTable';

import { useVDLStore } from '@VDL/store/VDLstore';

import { DEBUG_GRAPH_TABLE_EDIT, SHOW_AGREEMENT } from '@VDL/utils/consts';

import type {
  ILevel0DataObject,
  IObjectItem
} from '@VDL/api/types/ILevel0Data';

const columnHelper = createColumnHelper<ILevel0DataObject>();

export interface VDLLevel0GraphTableProps {
  isEdit: boolean;
}

export const VDLLevel0GraphTable = ({ isEdit }: VDLLevel0GraphTableProps) => {
  const {
    level0Data,
    selectedYear,
    agreementData,
    toSaveLevel0Data,
    setLevel0Data,
    setToSaveLevel0Data
  } = useVDLStore();

  const tableData = level0Data?.data;
  const tableHeaders = level0Data?.headers;

  const getColumns = useCallback(() => {
    const baseColumns = [
      columnHelper.accessor('number_pp', {
        header: '',
        cell: (info) => {
          const original = info.row.original;
          const agreementObject = agreementData?.find(
            (statusObject) => statusObject.code === original.code
          );

          return (
            <div className="flex w-full items-center justify-between gap-2">
              <Link
                to={`/${original.code}?year=${selectedYear}`}
                className="hover:text-primary flex items-center gap-2"
              >
                <IconLucide
                  name={original.icon_name}
                  className="h-4 w-4 shrink-0"
                />
                <span className="line-clamp-1">
                  {`${original.number_pp} ${original.name}`}
                </span>
              </Link>
              {SHOW_AGREEMENT && <div className="flex items-center">
                <VDLAgreementButton
                  agreementObject={agreementObject}
                  level0Code={original.code}
                />
              </div>}
            </div>
          );
        },
        size: 500
      })
    ];
    const periodColumns = tableHeaders
      ? Object.entries(tableHeaders).map(
          ([periodKey, headerData]: [string, any]) =>
            columnHelper.accessor(periodKey as keyof ILevel0DataObject, {
              header: headerData.name,
              cell: (info) => {
                const value = info.getValue();
                const originalRow = info.row.original;
                const isReversed = originalRow.is_reversed;

                if (typeof value === 'object') {
                  return (
                    <div className="flex w-full items-center justify-between gap-2">
                      {isEdit ? (
                        <EditableInput
                          info={info}
                          onBlur={(value) => {
                            const newLevel0Data = level0Data.data.map(
                              (row, index) => {
                                const currRow = level0Data.data[index];

                                if (index === info.row.index)
                                  return {
                                    ...currRow,
                                    [info.column.id]: {
                                      ...((currRow as any)[
                                        info.column.id
                                      ] as IObjectItem),
                                      position: value
                                    }
                                  };
                                else return row;
                              }
                            );
                            setLevel0Data({
                              ...level0Data,
                              data: newLevel0Data
                            });

                            if (selectedYear) {
                              const filtered = toSaveLevel0Data.filter(
                                (level0Item) =>
                                  level0Item.code !== originalRow.code
                              );

                              setToSaveLevel0Data([
                                ...filtered,
                                {
                                  code: originalRow.code,
                                  year: selectedYear,
                                  kv: info.column.id,
                                  value: value !== '' ? Number(value) : null
                                }
                              ]);
                            }
                          }}
                        />
                      ) : (
                        <span className="flex h-7 items-center px-[calc(.75rem_+_1px)] py-[calc(.25rem_+_1px)] font-semibold text-zinc-700">
                          {value.position}
                        </span>
                      )}
                      <div className="ml-auto flex h-full w-24 shrink-0 items-center">
                        {value.posLoc != null && value.position != null && (
                          <VDLLevel0TableArrowPosition
                            posLoc={value.posLoc}
                            position={value.position}
                          />
                        )}
                        {value.percent != null && (
                          <VDLLevel0TableNumberPosition
                            className="ml-auto"
                            percent={value.percent}
                            isReversed={isReversed}
                          />
                        )}
                      </div>
                    </div>
                  );
                }
              }
            })
        )
      : [];

    return [...baseColumns, ...periodColumns];
  }, [
    agreementData,
    selectedYear,
    tableHeaders,
    isEdit,
    level0Data,
    toSaveLevel0Data,
    setLevel0Data,
    setToSaveLevel0Data
  ]);

  const tableColumns = useMemo(() => getColumns(), [getColumns]);

  const { table } = useTanstackTable({
    data: tableData ?? [],
    columns: tableColumns,
    columnResizeMode: 'onChange'
  });

  return (
    <>
      <VDLBaseTable table={table} data={tableData ?? []} fullWidth={true} />
      {DEBUG_GRAPH_TABLE_EDIT && toSaveLevel0Data.length > 0 && (
        <div className="fixed bottom-4 left-4 z-50 rounded-sm bg-zinc-800 p-2 font-mono text-xs text-lime-400">
          <pre className="whitespace-pre">
            {toSaveLevel0Data.map((item) => JSON.stringify(item, undefined, 2))}
          </pre>
        </div>
      )}
    </>
  );
};

const EditableInput = ({
  info,
  onBlur
}: {
  info: CellContext<ILevel0DataObject, string | number | IObjectItem>;
  onBlur: (value: string) => void;
}) => {
  const initialValue = info.getValue() as IObjectItem;

  const [value, setValue] = useState(String(initialValue.position ?? ''));

  return (
    <Input
      className="h-7 min-w-14 font-semibold text-zinc-700"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={(e) => {
        const oldValue = initialValue.position;
        const newValue = e.target.value ? +e.target.value : null;
        if (oldValue !== newValue) onBlur(e.target.value);
      }}
    />
  );
};
